import { CurrencyCircleDollar, PencilSimple } from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Switch,
} from "@reactive-resume/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Helmet } from "react-helmet-async";

import { useToast } from "@/client/hooks/use-toast";
import { adminApi, type AdminPlan } from "@/client/services/admin";

export const AdminPlansPage = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [editPlan, setEditPlan] = useState<AdminPlan | null>(null);
  const [price, setPrice] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean>(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const {
    data: plans = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["admin-plans"],
    queryFn: () => adminApi.getPlans(),
  });

  const openEdit = (plan: AdminPlan) => {
    setEditPlan(plan);
    setPrice(String(plan.price));
    setIsActive(plan.isActive);
  };

  const closeEdit = () => {
    setEditPlan(null);
    setPrice("");
    setIsActive(true);
  };

  const handleSave = async () => {
    if (!editPlan) return;
    const priceNum = Number.parseFloat(price);
    if (Number.isNaN(priceNum) || priceNum < 0) {
      toast({
        variant: "error",
        title: "Invalid price",
        description: "Enter a non-negative number.",
      });
      return;
    }
    setSaving(true);
    try {
      await adminApi.updatePlan(editPlan.id, {
        price: priceNum,
        isActive,
      });
      toast({ variant: "success", title: "Plan updated." });
      closeEdit();
      void queryClient.invalidateQueries({ queryKey: ["admin-plans"] });
    } catch (error_: unknown) {
      const msg = error_ instanceof Error ? error_.message : "Failed to update plan.";
      toast({ variant: "error", title: "Error", description: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleSeedPlans = async () => {
    setSeeding(true);
    try {
      await adminApi.seedPlans();
      toast({ variant: "success", title: "Default plans created." });
      void queryClient.invalidateQueries({ queryKey: ["admin-plans"] });
    } catch (error_: unknown) {
      const msg = error_ instanceof Error ? error_.message : "Failed to seed plans.";
      toast({ variant: "error", title: "Error", description: msg });
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="space-y-6">
      <Helmet>
        <title>Admin Plans - VillageHut Resume</title>
      </Helmet>

      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">Plans</h1>
        <Button variant="outline" disabled={seeding} onClick={handleSeedPlans}>
          {seeding ? "Creating..." : "Create default plans"}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CurrencyCircleDollar className="size-5" />
            Payment plans
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && <p className="text-muted-foreground">Loading...</p>}
          {error && <p className="text-destructive">Failed to load plans.</p>}
          {!isLoading && !error && plans.length === 0 && (
            <div className="py-8 text-center text-muted-foreground">No plans found.</div>
          )}
          {!isLoading && !error && plans.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="px-4 py-3 text-left text-sm font-semibold">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Price</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Duration</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Active</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map((plan) => (
                    <tr key={plan.id} className="border-b">
                      <td className="px-4 py-3 text-sm font-medium">{plan.name}</td>
                      <td className="px-4 py-3 text-sm">KES {plan.price.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm">
                        {plan.durationDays == null ? "—" : `${plan.durationDays} days`}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={plan.isActive ? "success" : "secondary"}>
                          {plan.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            openEdit(plan);
                          }}
                        >
                          <PencilSimple className="mr-1 size-4" />
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!editPlan}
        onOpenChange={(open) => {
          if (!open) {
            closeEdit();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit plan</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-price">Price (KES)</Label>
              <Input
                id="edit-price"
                type="number"
                min={0}
                step={0.01}
                value={price}
                placeholder="0"
                onChange={(e) => {
                  setPrice(e.target.value);
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch id="edit-active" checked={isActive} onCheckedChange={setIsActive} />
              <Label htmlFor="edit-active">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeEdit}>
              Cancel
            </Button>
            <Button disabled={saving} onClick={handleSave}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
